import  React, { useEffect, useRef, useMemo, useState } from 'react';
import { Alert, Card, Form, Spin, Layout, Input, Radio, message } from 'antd';
import { Routes } from '../../../routes'; 
import Spinner from '../../components/Spinner/Spinner';
import useHasError from '../../hooks/useHasError'; 
import useIsLoading from '../../hooks/useIsLoading'; 
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import SearchSelector from '../../components/SearchSelector/SearchSelector';
import FormSaveCancelToolbar from '../../components/FormSaveCancelToolbar/FormSaveCancelToolbar';
import useComponentListSetCreate from '../../gql/hooks/useComponentListSetCreate';
import useComponentListSetPatch from '../../gql/hooks/useComponentListSetPatch';
import useComponentListSet from '../../gql/hooks/useComponentListSet';
import useComponentListPatch from '../../gql/hooks/useComponentListPatch';
import { flags } from '../../../featureFlags/flags'; 
import { useFeatureFlags } from '../../../featureFlags';
import FormSegmentation from '../../components/FormSegmentation/FormSegmentation'; 
import FormCompanyRelationship from '../../components/FormCompanyRelationship/FormCompanyRelationship'; 
import FormProducts from '../../components/FormProducts/FormProducts'; 
import FormBrands from '../../components/FormBrands/FormBrands'; 
import { authService } from '../../../auth/authService';
import FormEmployeeLocales from '../../components/FormEmployeeLocales/FormEmployeeLocales'; 
import FormCustomerLocales from '../../components/FormCustomerLocales/FormCustomerLocales'; 
import FormLearningProfiles from '../../components/FormLearningProfiles/FormLearningProfiles'; 
import useComponentListSetDelete from '../../gql/hooks/useComponentListSetDelete';
import './styles.less';

const initialValues = {
    name: '',
    isPublished: false,
    type: 'component-list-set',

    // Uses syncObjectRelationships
    segmentIds: [],                    // Matches what is on server
    segmentIdsModified: [],            // Do not save, internal use only
    segmentIdsAreModified: false,      // Do not save, internal use only
    
    // Uses syncObjectRelationships
    companyRelationshipIds: [],                    // Matches what is on server
    companyRelationshipIdsModified: [],            // Do not save, internal use only
    companyRelationshipIdsAreModified: false,      // Do not save, internal use only
    
    // Uses syncObjectRelationships
    productIds: [],                    // Matches what is on server
    productIdsModified: [],            // Do not save, internal use only
    productIdsAreModified: false,      // Do not save, internal use only
   
    // Uses syncObjectRelationships
    learningProfileIds: [],                    // Matches what is on server
    learningProfileIdsModified: [],            // Do not save, internal use only
    learningProfileIdsAreModified: false,      // Do not save, internal use only

    // Uses syncObjectRelationships
    employeeLocaleIds: [],                    // Matches what is on server
    employeeLocaleIdsModified: [],            // Do not save, internal use only
    employeeLocaleIdsAreModified: false,      // Do not save, internal use only
    
    brandIds: [],
    brandIdsModified: [],
    brandIdsAreModified: false
};

const ComponentsEditPage = (  { route: { params: { clsID } } } ) => {
    const [ form ] = Form.useForm(); 
    const formProductsRef = useRef();
    const formSegmentationRef = useRef();
    const formLearningProfilesRef = useRef();
    const formLocalesRef = useRef();
    const formBrandsRef = useRef(); 
    const formEmployeeLocalesRef = useRef();
    const formCompanyRelationshipRef = useRef();
    const [ id, setId ] = useState( clsID );
    const [ isCreating, setIsCreating ] = useState( !clsID );
    const { createCLS } = useComponentListSetCreate();
    const { patchCLS } = useComponentListSetPatch();
    const { patchCList } = useComponentListPatch();
    const [ formReady, setFormReady ] = useState( !clsID );
    const { loadCLSet, clsDataSet, clsError, clsLoading } = useComponentListSet();
    const { hasError } = useHasError( { items: [ clsError ] } );
    const { isLoading } = useIsLoading( { items: [ clsLoading ] } );
    const [ showRosterGroups ] = useFeatureFlags( [ flags.SEGMENTATION_CLIENT_MANAGED ] );
    const [ showCompanyRelationship ] = useFeatureFlags( [ flags.SEGMENTATION_COMPANY_RELATIONSHIP ] );
    const [ showProducts ] = useFeatureFlags( [ flags.SEGMENTATION_PRODUCT ] );
    const [ showLocales ] = useFeatureFlags( [ flags.SEGMENTATION_LOCALES ] );
    const [ showLearningProfiles ] = useFeatureFlags( [ flags.SEGMENTATION_LEARNING_PROFILES ] );
    const [ courseIds, setCourseIds ] = useState( [] );
    const [ curriculaIds, setCurriculaIds ] = useState( [] );
    const [ isSaving, setIsSaving ] = useState( false );
    const [ messageApi, contextHolder ] = message.useMessage();
    const { deleteComponentList } = useComponentListSetDelete();
    
    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: authService.isHoya()  ? 'My Journey Setup' : 'Homepage Setup', path: Routes.ComponentsList.buildPath() },
        { name: isCreating ? 
            authService.isHoya()  ? 'Create My Journey' : 'Create Homepage List' : 
            authService.isHoya()  ? 'Edit My Journey' : 'Edit Homepage List' }
    ];

    useEffect( () => {
        if ( isCreating || !id ) {
            return;
        }
        setTimeout( () => {
            loadCLSet( id );
        }, 700 );
    }, [ id ] );

    const handleSubmit = async ( values ) => {
        if ( authService.isHoya() && formBrandsRef?.current && !formBrandsRef.current.validate() ) {
            document.getElementById( 'brand-alert' ).scrollIntoView( {
                behavior: 'smooth', // For smooth, animated scrolling
                block: 'center',    // Aligns the element to the center of the viewport
                inline: 'nearest'   // Aligns the element to the nearest edge
            } );
            return;
        }
        setIsSaving( true );

        let hasError = false;

        if ( isCreating ) {
            const payload = {
                name: values.name,
                type: 'HOMEPAGE_SETUP',
                isPublished: false,
                description: ''
            };

            try {
                const response = await createCLS( payload );
                
                if ( response.success ) {
                    const id = response.response.componentListSetId;
                    setId( id );
                    setIsCreating( false ); 
                    history.replaceState( {}, '', 
                        window.location.href.replace( 'create', `edit/${ id }` ) );
 
                } else { 
                    hasError = true;
                    messageApi.error( 'Error creating component list' );
                }
            // eslint-disable-next-line no-unused-vars
            } catch ( e ) { 
                hasError = true;
                messageApi.error( 'Error creating component list' );
            }  
        } else {
            const payload = {
                name: values.name,
                type: 'HOMEPAGE_SETUP',
                componentListSetId: id,
                isPublished: values.isPublished,
                description: values.description
            };
            try {
                const response = await patchCLS( payload );
                
                if ( response.success ) {
                    const id = response.response;
                    setId( id );
                    setIsCreating( false ); 
                } else { 
                    hasError = true;
                    messageApi.error( 'Error saving component list' );
                }
            // eslint-disable-next-line no-unused-vars
            } catch ( e ) { 
                hasError = true;
                messageApi.error( 'Error saving component list' );
            }  
            
            // Save Course UUIDs
            if ( coursesList && JSON.stringify( coursesList.uuids ) !== JSON.stringify( courseIds ) ) {
                const coursePayload = {
                    componentListId: coursesList.componentListId,
                    type: 'COURSES',
                    uuids: courseIds
                };

                try {
                    const response = await patchCList( coursePayload );

                    if ( response.success ) {
                        const id = response.response.componentListSetId;
                        setId( id );
                        setIsCreating( false ); 
                    } else { 
                        hasError = true;
                        messageApi.error( 'Error saving component list' );
                    }
                // eslint-disable-next-line no-unused-vars
                } catch ( e ) { 
                    hasError = true;
                    messageApi.error( 'Error saving component list' );
                }  
            }

            // Save Curricula UUIDs
            if ( curriculaList && JSON.stringify( curriculaList.uuids ) !== JSON.stringify( curriculaIds ) ) {
                const coursePayload = {
                    componentListId: curriculaList.componentListId,
                    type: 'CURRICULUMS',
                    uuids: curriculaIds
                };

                try {
                    const response = await patchCList( coursePayload );

                    if ( response.success ) {
                        const id = response.response.componentListSetId;
                        setId( id );
                        setIsCreating( false ); 
                    } else { 
                        hasError = true;
                        messageApi.error( 'Error saving component list' );
                    }
                // eslint-disable-next-line no-unused-vars
                } catch ( e ) { 
                    hasError = true;
                    messageApi.error( 'Error saving component list' );
                }  
            }

            if ( showRosterGroups && formSegmentationRef.current.isModified() ) {
                const result = await formSegmentationRef.current.syncData();
            
                if ( !result ) {
                    hasError = true;
                }
            }

            if ( showCompanyRelationship && formCompanyRelationshipRef.current.isModified() ) {
                const result = await formCompanyRelationshipRef.current.syncData();

                if ( !result ) {
                    hasError = true;
                }
            }
            
            if ( showProducts && formProductsRef.current.isModified() ) {
                const result = await formProductsRef.current.syncData();

                if ( !result ) {
                    hasError = true;
                }
            }
        
            if ( showLearningProfiles && formLearningProfilesRef.current.isModified() ) {
                const result = await formLearningProfilesRef.current.syncData();

                if ( !result ) {
                    hasError = true;
                }
            }
        
            if ( showLocales && formEmployeeLocalesRef.current.isModified() ) {
                const result = await formEmployeeLocalesRef.current.syncData();

                if ( !result ) {
                    hasError = true;
                }
            }

            if ( showLocales && formLocalesRef.current.isModified() ) {
                const result = await formLocalesRef.current.syncData();

                if ( !result ) {
                    hasError = true;
                }
            }

            if ( authService.isHoya() && formBrandsRef?.current && formBrandsRef.current.isModified() ) {
                const result = await formBrandsRef.current.syncData();

                if ( !result ) {
                    hasError = true;
                }
            }

            if ( !hasError ) {
                messageApi.success( 'Saved.' );
                window.history.back();
            }
        }

        setIsSaving( false );
    };

    const handleSave = () => {
        form.submit();
    };

    const handleDelete = async () => {
        await deleteComponentList( id );
        window.history.back();
    };

    const formEditValues = useMemo( () => {
        if ( !clsDataSet || clsDataSet.length === 0 ) {
            return { ...initialValues };
        } else {    
            setFormReady( true );

            return {
                ...clsDataSet, 
                id: id,
                type: 'component-list-set',
                segmentIdsAreModified: false,
                segmentIdsModified: [],
                companyRelationshipIdsModified: [],            
                companyRelationshipIdsAreModified: false,      
                productIdsModified: [],            
                productIdsAreModified: false,      
                learningProfileIdsModified: [],            
                learningProfileIdsAreModified: false,      
                employeeLocaleIdsModified: [],            
                employeeLocaleIdsAreModified: false     
            };
        }
    }, [ clsDataSet ] );
    
    const coursesList = useMemo( () => {
        if ( clsDataSet && clsDataSet.componentLists ) {
            const item = clsDataSet.componentLists.find( i => i.type === 'COURSES' );
            
            if ( item ) {
                setCourseIds( item.uuids );
                return item;
            }
        } else {
            return undefined;
        }
    }, [ clsDataSet ] );
    
    const curriculaList = useMemo( () => {
        if ( clsDataSet && clsDataSet.componentLists ) {
            const item = clsDataSet.componentLists.find( i => i.type === 'CURRICULUMS' );

            if ( item ) {
                setCurriculaIds( item.uuids );
                return item;
            }
        } else {
            return undefined;
        }
    }, [ clsDataSet ] );

    const handleModifiedChange = () => {

    };

    const saveReorderCoursesCallback = ( items ) => {
        setCourseIds( items );
    };
    
    const saveReorderCurriculaCallback = ( items ) => {
        setCurriculaIds( items );
    };

    /******************************
     * ANCHOR Transfer Events
     *****************************/
    
    const handleCoursesChange = ( reorderedTargetKeys ) => {
        setCourseIds( reorderedTargetKeys );
    };
    
    const handleCurriculaChange = ( reorderedTargetKeys ) => {
        setCurriculaIds( reorderedTargetKeys );
    };

    /*************************************
     *  ANCHOR Render
     *************************************/
   
    return ( 
        <Layout className="component-edit-page">
            
             { contextHolder }

            { /* Page header breadcrumbs */ }
            <Breadcrumbs crumbs={ breadcrumbs }/>

            { /* Loading Spinner */ }
            { isLoading &&
                <Spinner/>
            }
             
            { /* Main Content */ }
            { !isLoading &&
                <Spin spinning={ isSaving }>
                    <Card
                        className="component-edit-content"
                        title={ authService.isHoya()  ? 'My Journey' : 'Homepage List' }
                    >   
                        { /* Error message */ }
                        { hasError && 
                            <Alert 
                                type="error"
                                message="Error"
                                description="An unknown error occurred. Please try again later."
                            />
                        }

                        { !hasError && formReady &&
                            <div className="tab-main-columns">
                                <div className="left">
                                    <Form
                                        form={ form }
                                        name="session"
                                        layout="vertical"
                                        initialValues={ formEditValues  }
                                        onFinish={ handleSubmit }
                                        scrollToFirstError
                                    >   
                                        <Form.Item
                                            label="Name"
                                            name="name"
                                            maxLength= { 50 }
                                            style={ { marginLeft: '10px', paddingRight: '11px', marginBottom: '36px' } }
                                            rules={ [
                                                {
                                                    required: true,
                                                    message: 'A name is required',
                                                },
                                                {
                                                    max: 50,
                                                    type: 'string',
                                                    message: 'Names must be 50 characters or shorter.',
                                                }
                                            ] }
                                        >
                                            <Input 
                                                placeholder="Name"    
                                            />
                                        </Form.Item> 

                                        { !isCreating &&
                                            <>
                                                <Card
                                                    title="Publishing"
                                                > 
                                                    <Form.Item 
                                                        name="isPublished"
                                                        rules={ [
                                                            {
                                                                message: 'You must add one or more objects below before publishing',
                                                                validateTrigger: [],
                                                                validator: ( _, value ) => {
                                                                    if ( !value ) {
                                                                        return Promise.resolve();
                                                                    } else {
                                                                        const crs = courseIds.length > 0 ? true : false;
                                                                        const crk = curriculaIds.length > 0 ? true : false;
                                                                        
                                                                        if ( coursesList && curriculaList ) {
                                                                            if ( crs  || crk ) {
                                                                                return Promise.resolve();
                                                                            } else {
                                                                                return Promise.reject();
                                                                            }
                                                                        }

                                                                        if ( coursesList && !curriculaList ) {
                                                                            if ( crs ) {
                                                                                return Promise.resolve();
                                                                            } else {
                                                                                return Promise.reject();
                                                                            }
                                                                        }
                                                                        
                                                                        if ( !coursesList && curriculaList ) {
                                                                            if ( crk ) {
                                                                                return Promise.resolve();
                                                                            } else {
                                                                                return Promise.reject();
                                                                            }
                                                                        } 
                                                                    }
                                                                }
                                                            },
 
                                                        ] }
                                                    >
                                                        <Radio.Group  
                                                            size="medium"
                                                            buttonStyle="solid" 
                                                        >
                                                            <Radio.Button value={ true }>
                                                                Published
                                                            </Radio.Button>
                                                            <Radio.Button value={ false }>
                                                                Unpublished
                                                            </Radio.Button>
                                                        </Radio.Group>
                                                    </Form.Item>
                                                </Card>
    
                                                { coursesList && 
                                                    <Card
                                                        title="Course List"
                                                    >  
                                                        <SearchSelector 
                                                            type="courses"  
                                                            reorder={ true }
                                                            showType={ false }
                                                            initialItems={ courseIds }
                                                            onChange={ handleCoursesChange }
                                                            saveReorderCallback={ saveReorderCoursesCallback }
                                                        />
                                                    </Card>  
                                                }
                                                
                                                { curriculaList &&
                                                    <Card
                                                        title="Curricula List"
                                                    > 
                                                        <SearchSelector 
                                                            type="curricula"  
                                                            reorder={ true }
                                                            showType={ false }
                                                            initialItems={ curriculaIds }
                                                            onChange={ handleCurriculaChange }
                                                            saveReorderCallback={ saveReorderCurriculaCallback }
                                                        />
                                                    </Card>  
                                                }

                                                <Card
                                                    title="Segments"
                                                > 
                                                    { showRosterGroups && !isLoading &&
                                                        <div
                                                            style={ { minHeight: '350px' } }
                                                        >
                                                            <FormSegmentation 
                                                                form={ form }
                                                                ref={ formSegmentationRef }
                                                                modifiedCallback={ handleModifiedChange } 
                                                            />
                                                        </div>
                                                    }

                                                    { authService.isHoya() &&
                                                        <FormBrands
                                                            ref={ formBrandsRef }
                                                            form={ form }
                                                            hideSeiko={ true }
                                                            modifiedCallback={ handleModifiedChange }
                                                        />
                                                    }

                                                    { showCompanyRelationship &&
                                                        <FormCompanyRelationship 
                                                            form={ form }
                                                            ref={ formCompanyRelationshipRef }
                                                            modifiedCallback={ handleModifiedChange } 
                                                        />
                                                    }

                                                    { showProducts &&
                                                        <FormProducts 
                                                            form={ form }
                                                            ref={ formProductsRef }
                                                            modifiedCallback={ handleModifiedChange } 
                                                        />
                                                    }

                                                    { showLearningProfiles &&
                                                        <FormLearningProfiles 
                                                            form={ form }
                                                            ref={ formLearningProfilesRef }
                                                            modifiedCallback={ handleModifiedChange } 
                                                        />
                                                    }

                                                    { showLocales &&
                                                        <FormCustomerLocales 
                                                            form={ form }
                                                            ref={ formLocalesRef }
                                                            modifiedCallback={ handleModifiedChange } 
                                                        />
                                                    }

                                                    { showLocales && authService.isHoya() &&
                                                        <FormEmployeeLocales 
                                                            form={ form }
                                                            ref={ formEmployeeLocalesRef }
                                                            modifiedCallback={ handleModifiedChange } 
                                                        />
                                                    }
                                                </Card>
                                            </>
                                        }
                                    </Form>
                                </div>
                            
                                <div className="right">
                                    <FormSaveCancelToolbar
                                        form={ form }
                                        saving={ isLoading } 
                                        modified={ true }
                                        showCancelButton={ true }
                                        showDeleteButton={ true }
                                        saveCallback={ handleSave } 
                                        deleteCallback={ handleDelete } 
                                    />
                                </div>
                            </div>
                        }
                    </Card>
                </Spin>
            }
        </Layout>
    );
};

export default ComponentsEditPage;
